import { useCallback, useMemo, useState } from 'react';
import classNames from 'classnames';

import CamError from 'components/CamError';
import CamHostAd from 'components/CamHostAd/CamHostAd';
import CamPlayerV2 from 'components/CamPlayerV2';
import CamTimeout from 'components/CamPlayerV2/CamTimeout';
import { AutoplayOptions, PlayerSkins } from 'components/CamPlayerV2/constants';
import MultiCamPaywall from 'components/MultiCamPaywall';
import { getSpotDetails } from 'selectors/spot';
import { useUserPermissionStatus } from 'selectors/user';
import { useAppSelector } from 'stores/hooks';
import type { Camera } from 'types/camera';
import type { SpotDetails } from 'types/spot';
import getCamStillImage from 'utils/getCamStillImage';

import styles from './CamPlayerKbygRewindsMultiCam.module.scss';

const containerClassName = (numCamsDisplayed: number) =>
  classNames({
    [styles.container]: true,
    [styles.containerSingleView]: numCamsDisplayed === 1,
    [styles.containerDoubleView]: numCamsDisplayed === 2,
    [styles.containerTripleView]: numCamsDisplayed === 3,
    [styles.containerQuadView]: numCamsDisplayed === 4,
  });

const camContainerClassName = (isPrimary: boolean, camNum: number, showPaywall: boolean) => {
  const secondaryCamNumClass = styles[
    `containerSecondary${camNum + 1}` as keyof typeof styles
  ] as string;
  return classNames({
    [styles.containerSecondary]: !isPrimary,
    [styles.containerPrimary]: isPrimary,
    [secondaryCamNumClass]: !isPrimary && !!secondaryCamNumClass,
    [styles.containerPaywall]: !showPaywall,
  });
};

type Props = {
  disablePaywalls?: boolean;
  multiCamViewActive: boolean;
  numCamsDisplayed: number;
  onToggleStreaming: (streaming: boolean) => void;
  primaryCam: Camera;
  sortedCameras: Camera[];
  autoplay?: boolean;
};

export const CamPlayerKbygRewindsMultiCam: React.FC<Props> = ({
  disablePaywalls,
  multiCamViewActive,
  numCamsDisplayed,
  onToggleStreaming,
  primaryCam,
  sortedCameras,
  autoplay,
}) => {
  const { hasAdSupportedCamPermissions, hasAdFreeCamPermissions: hasAdFreeCamPerm } =
    useUserPermissionStatus();
  const hasAdFreeCamPermissions = disablePaywalls || hasAdFreeCamPerm;
  const showPaywall = !hasAdFreeCamPermissions && !hasAdSupportedCamPermissions;
  const spot: SpotDetails = useAppSelector(getSpotDetails);
  const [camTimeout, setCamTimeout] = useState<boolean>(false);

  const paywallSegmentProperties = useMemo(
    () => ({
      category: 'cams & reports',
      location: 'multi cam view',
      pageName: 'Spot Report',
      spotId: spot?._id,
      spotName: spot?.name,
      subregionId: spot?.subregion?._id,
    }),
    [spot],
  );

  const cameras = useMemo(
    () =>
      sortedCameras.map((cam, camNum) => {
        const isPrimaryCam = cam._id === primaryCam._id || numCamsDisplayed === 1;
        const camStillUrl = getCamStillImage(cam);
        const { highlights: _highlights, ...cameraWithoutHighlights } = cam;
        const livePlayerProps = {
          CamErrorComponent: CamError as unknown as React.ReactNode,
          autoplay:
            !autoplay || showPaywall
              ? AutoplayOptions.AUTOPLAY_DISABLED
              : AutoplayOptions.AUTOPLAY_ENABLED,
          camera: cameraWithoutHighlights,
          camHostAd: <CamHostAd camera={cam} spot={spot} isMultiCam />,
          dontTrack: true,
          isPremium: true,
          isMultiCam: true,
          latestRewind: false,
          location: 'Kbyg Rewinds',
          playerId: `${cam._id}-kbyg-rewinds-multiCam`,
          playerSkin: !autoplay ? PlayerSkins.NONE : PlayerSkins.MINIMAL,
          showOverlays: true,
          showRewindLink: false,
          showInsightsSmartRewindsIcon: true,
          isPrimaryCam,
          multiCamViewActive,
          timeoutMultiCams: () => setCamTimeout(true),
          onToggleStreaming,
        };

        return (
          <div
            key={`${cam._id}-multi-cam-camera`}
            data-testid={`cam-container-${cam._id}`}
            className={camContainerClassName(isPrimaryCam, camNum, !showPaywall)}
          >
            {camTimeout || showPaywall ? (
              <div
                data-testid="cam-still"
                className={styles.camStill}
                style={{ backgroundImage: `url(${camStillUrl})` }}
              />
            ) : (
              <div className="sl-section-full-bleed">
                <CamPlayerV2 {...livePlayerProps} />
              </div>
            )}
          </div>
        );
      }),
    [
      camTimeout,
      showPaywall,
      multiCamViewActive,
      numCamsDisplayed,
      onToggleStreaming,
      primaryCam._id,
      sortedCameras,
      spot,
      autoplay,
    ],
  );

  const handleTimeoutClick = useCallback(() => setCamTimeout(false), []);

  return (
    <div className={containerClassName(numCamsDisplayed)} data-testid="multi-cam-container">
      {showPaywall && <MultiCamPaywall segmentProperties={paywallSegmentProperties} />}
      {camTimeout && <CamTimeout isMultiCam onClick={handleTimeoutClick} />}
      {cameras}
    </div>
  );
};
